import "./Departments.scss";
import { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { DepartmentColumns } from "../../SourceData";
import { InputAdornment, TextField, useMediaQuery } from "@mui/material";
import AddGif from "./../../assets/add-gif.gif";
import BlueButton from "../../components/BlueButton/BlueButton";
import SearchIcon from "@mui/icons-material/Search";
import Delete from "./../../assets/delete.svg";
import Edit from "./../../assets/edit-user.svg";
import MessageModals from "../../components/MessageModals/MessageModals";
import SwipeableModals from "./SwipeableModals/SwipeableModals";
import SwitchButton from "../../components/SwitchButton/SwitchButton";
import api from "../../utils/api";
import { usePermissions } from "./../../hooks/usePermissions";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const Categories = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");
  const [query, setQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isSwipeableOpen, setIsSwipeableOpen] = useState(false);
  const [Swipeable, setSwipeable] = useState("");

  // States for delete functionality
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Categories data state
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleOpenSwipeable = (view, category = null) => {
    if (!hasPermission(42)) {
      toast.error("ليس لديك هذه الصلاحية");
      return;
    }
    if (category) {
      setSelectedCategory(category);
    }
    setSwipeable(view);
    setIsSwipeableOpen(true);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setQuery(value);

    // Only update searchTerm if query is empty or has more than 2 characters
    if (value === "" || value.length > 2) {
      setSearchTerm(value);
      // Reset to first page when filters change
      setPage(1);
    }
  };

  // Fetch categories data
  const fetchCategories = async () => {
    if (!hasPermission(40)) {
      return;
    }
    setLoading(true);
    try {
      let params = {
        page,
        limit: pageSize,
      };

      // Add search term if it's not empty
      if (searchTerm) {
        params.name = searchTerm;
        params.local = "ar";
      }

      const response = await api.get("/categories", { params });

      if (response.data.status === "success") {
        setCategories(response.data.data);
        setTotalCount(response.data.totalCount);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  };

  // Effect to fetch categories when dependencies change
  useEffect(() => {
    fetchCategories();
  }, [page, pageSize, searchTerm]);

  const handleActive = async (id) => {
    if (!hasPermission(41)) {
      toast.error("ليس لديك هذه الصلاحية");
      return;
    }
    setLoading(true);
    try {
      await api.patch(`/categories/${id}/toggleCategory`);
      fetchCategories();
    } catch (error) {
      console.error("Error toggling category status:", error);
    } finally {
      setLoading(false);
    }
  };

  const actionColumn = [
    {
      field: "isActive",
      headerName: "الحالة",
      minWidth: 60,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const isActive = params?.row?.isActive === true;
        return (
          <SwitchButton
            checked={isActive}
            onClick={() => handleActive(params.row._id)}
          />
        );
      },
    },
    {
      field: "action",
      headerName: "",
      width: 100,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <div className="cell-action">
            <div
              className="cell-btn"
              onClick={() => {
                handleOpenSwipeable("editCategory", params.row);
              }}
            >
              <img src={Edit} alt="تعديل" />
            </div>
            <div
              className="cell-btn"
              onClick={() => {
                if (!hasPermission(43)) {
                  toast.error("ليس لديك هذه الصلاحية");
                  return;
                }
                setSelectedCategory(params.row);
                setOpenDelete(true);
              }}
            >
              <img src={Delete} alt="حذف" />
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div className="departments">
      <div className="departments-header">
        <p className="title">
          الاقسام <span className="count-badge">{totalCount || 0}</span>
        </p>
      </div>
      <div className="departments-container">
        <div className="actions-container">
          <div className="search">
            <TextField
              id="outlined-search"
              type="search"
              value={query}
              placeholder="ابحث باسم القسم...."
              onChange={handleSearchChange}
              sx={{
                width: isMobile ? "100%" : isTablet ? "300px" : "360px",
                borderRadius: "8px",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <BlueButton
            text="اضافة قسم جديد"
            size={isMobile ? "full" : isTablet ? "fit" : "large"}
            variant="fill"
            onClick={() => {
              if (!hasPermission(39)) {
                toast.error("ليس لديك هذه الصلاحية");
                return;
              }
              handleOpenSwipeable("createCategory");
            }}
            icon={<img src={AddGif} alt="" />}
          />
        </div>

        <DataGrid
          sx={{ backgroundColor: "white", width: "100%" }}
          autoHeight={true}
          columns={DepartmentColumns.concat(actionColumn)}
          rows={categories.map((category, index) => ({
            ...category,
            // Generate unique id if not present
            id: category._id || index,
            // Add index for numbering
            index: (page - 1) * pageSize + index + 1,
          }))}
          disableVirtualization
          disableColumnFilter
          disableColumnMenu
          loading={loading}
          pagination
          paginationMode="server"
          rowCount={totalCount}
          initialState={{
            pagination: {
              paginationModel: { pageSize, page: page - 1 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          onPaginationModelChange={(model) => {
            // Handle page change
            if (model.page !== page - 1) {
              setPage(model.page + 1);
            }

            // Handle page size change
            if (model.pageSize !== pageSize) {
              setPageSize(model.pageSize);
              setPage(1); // Reset to first page when changing page size
            }
          }}
          getRowClassName={(params) => "data-grid-row"}
          localeText={{
            MuiTablePagination: {
              labelRowsPerPage: "قسم في الصفحة",
            },
          }}
          rowHeight={80}
        />

        {/* Swipeable Modal for Create/Edit */}
        <SwipeableModals
          open={isSwipeableOpen}
          onClose={() => {
            setIsSwipeableOpen(false);
            setSelectedCategory(null);
          }}
          onOpen={() => setIsSwipeableOpen(true)}
          currentView={Swipeable}
          categoryData={selectedCategory}
          onDataChange={fetchCategories}
        />

        {/* Delete Confirmation Modal */}
        <MessageModals
          open={openDelete}
          currentView="delete"
          deleteMessage={"تم حذف القسم بنجاح"}
          route={selectedCategory ? `/categories/${selectedCategory._id}` : ""}
          onDataChange={fetchCategories}
          onClose={() => {
            setOpenDelete(false);
            setSelectedCategory(null);
          }}
        />
      </div>
    </div>
  );
};

export default Categories;
